import React, {useState} from 'react';
import {useAppSelector} from '../../hooks/redux';
import {selectTests} from '../../slices/testSlice';
import {getCategoryStats} from '@/utils/healthCategories';
import {useDashboardData} from '@/hooks/useDashboardData';
import {Info} from 'lucide-react';

interface HorizontalCategoryChartProps {
    selectedCategory?: string;
    onCategorySelect?: (categoryKey: string) => void;
}

// Category descriptions explaining what each score represents
const CATEGORY_DESCRIPTIONS: Record<
    string,
    {
        description: string;
        calculation: string;
        interpretation: string;
    }
> = {
    heart: {
        description:
            'Measures cardiovascular health markers including cholesterol, triglycerides, and inflammation indicators.',
        calculation:
            'Each test is scored 0-100 based on where it falls within its reference range (0 = low end, 50 = middle, 100 = high end). The category score is the average of all tests.',
        interpretation:
            'Higher scores (closer to 100) indicate values are toward the upper end of normal ranges. A score around 50 means values are centered in the normal range. Optimal varies by marker.',
    },
    'liver-kidney': {
        description:
            'Tracks liver enzymes (ALT, AST, ALP) and kidney function markers (creatinine, BUN) that indicate organ health.',
        calculation:
            'Each test is scored 0-100 based on where it falls within its reference range (0 = low end, 50 = middle, 100 = high end). The category score is the average of all tests.',
        interpretation:
            'For most liver/kidney markers, lower-to-mid range values are preferred. High scores may warrant attention if driven by elevated enzymes.',
    },
    micronutrient: {
        description:
            'Evaluates essential vitamins and minerals like Vitamin D, B12, Iron, and Ferritin that support overall health.',
        calculation:
            'Each test is scored 0-100 based on where it falls within its reference range (0 = low end, 50 = middle, 100 = high end). The category score is the average of all tests.',
        interpretation:
            'Mid-range to higher scores are generally favorable, indicating adequate nutrient levels. Low scores may suggest deficiencies worth addressing.',
    },
    hormone: {
        description:
            'Monitors key hormones including thyroid (TSH, T3, T4), reproductive hormones, and stress markers like cortisol.',
        calculation:
            'Each test is scored 0-100 based on where it falls within its reference range (0 = low end, 50 = middle, 100 = high end). The category score is the average of all tests.',
        interpretation:
            'Optimal hormone levels vary by age and gender. Mid-range scores typically indicate balanced hormone function.',
    },
    metabolic: {
        description: 'Assesses metabolic health through glucose, HbA1c, insulin, and electrolyte balance indicators.',
        calculation:
            'Each test is scored 0-100 based on where it falls within its reference range (0 = low end, 50 = middle, 100 = high end). The category score is the average of all tests.',
        interpretation:
            'Lower scores for glucose/HbA1c are generally better. Electrolytes should be mid-range. High scores in glucose markers may indicate metabolic concerns.',
    },
};

// Map category keys to their corresponding colors
const CATEGORY_COLORS: Record<string, string> = {
    heart: '#ef829c',
    micronutrient: '#ebc191',
    'liver-kidney': '#8fdbb6',
    hormone: '#a0e4dd',
    metabolic: '#c7b8de',
    other: '#94a3b8', // Default gray for other categories
};

// Tooltip component for category info
const CategoryInfoTooltip: React.FC<{
    categoryKey: string;
    categoryName: string;
    onClose: () => void;
}> = ({categoryKey, categoryName, onClose}) => {
    const info = CATEGORY_DESCRIPTIONS[categoryKey];
    if (!info) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-xl max-w-sm mx-4 p-5 border border-gray-200"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-blue-950">{categoryName}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 -mr-1 -mt-1">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="space-y-3 text-sm">
                    <div>
                        <p className="text-gray-600">{info.description}</p>
                    </div>

                    <div>
                        <p className="font-medium text-blue-950 mb-1">How it&apos;s calculated</p>
                        <p className="text-gray-600">{info.calculation}</p>
                    </div>

                    <div>
                        <p className="font-medium text-blue-950 mb-1">How to interpret</p>
                        <p className="text-gray-600">{info.interpretation}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const HorizontalCategoryChart: React.FC<HorizontalCategoryChartProps> = ({selectedCategory, onCategorySelect}) => {
    const testsState = useAppSelector(selectTests);
    const isLoading = testsState.loading;
    const [tooltipCategory, setTooltipCategory] = useState<{key: string; name: string} | null>(null);

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: currentPatientData} = useDashboardData();

    // Get all results for the current patient
    const allResults = React.useMemo(() => {
        return currentPatientData.flatMap(
            (test: any) =>
                test.results?.map((result: any) => ({
                    ...result,
                    createdDate: test.createdDate,
                })) || []
        );
    }, [currentPatientData]);

    // Get category stats
    const categoryStats = getCategoryStats(allResults);

    // Include all categories except 'other', sorted by score (highest first)
    // Empty categories will be shown but not clickable
    const categoryData = React.useMemo(() => {
        return categoryStats
            .filter((category) => category.key !== 'other')
            .map((category) => ({
                key: category.key,
                name: category.name,
                value: Math.round(category.averageValue),
                isSelected: selectedCategory === category.key,
                hasTests: category.availableTests > 0,
            }))
            .sort((a, b) => b.value - a.value); // Sort by score descending
    }, [categoryStats, selectedCategory]);

    // Calculate the width needed for the longest category name
    const maxNameWidth = React.useMemo(() => {
        if (categoryData.length === 0) return 'auto';
        const longestName = categoryData.reduce(
            (longest, current) => (current.name.length > longest.length ? current.name : longest),
            ''
        );
        // Approximate width: 8px per character + padding
        return `${longestName.length * 8 + 32}px`;
    }, [categoryData]);

    // Handle category click
    const handleCategoryClick = (categoryKey: string, hasTests: boolean) => {
        if (onCategorySelect && hasTests) {
            onCategorySelect(categoryKey);
        }
    };

    if (isLoading) {
        return (
            <div className="space-y-3">
                {Array.from({length: 5}).map((_, index) => (
                    <div key={index} className="animate-pulse">
                        <div className="flex items-center justify-between mb-1">
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                            <div className="h-4 bg-gray-200 rounded w-8"></div>
                        </div>
                        <div className="h-6 bg-gray-200 rounded"></div>
                    </div>
                ))}
            </div>
        );
    }

    if (categoryData.length === 0) {
        return (
            <div className="flex items-center justify-center h-32 text-gray-500">
                <p className="text-sm">No category data available</p>
            </div>
        );
    }

    return (
        <>
            {/* Tooltip modal */}
            {tooltipCategory && (
                <CategoryInfoTooltip
                    categoryKey={tooltipCategory.key}
                    categoryName={tooltipCategory.name}
                    onClose={() => setTooltipCategory(null)}
                />
            )}
            <div>
                {categoryData.map((category) => (
                    <div
                        key={category.key}
                        className={`transition-all duration-200 border-b border-gray-200 last:border-b-0 relative ${
                            category.hasTests ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-50'
                        }`}
                        onClick={() => handleCategoryClick(category.key, category.hasTests)}>
                        <div className="flex">
                            {/* Left section: Category name with triangle indicator */}
                            <div
                                className="py-3 pr-4 border-r border-gray-200 whitespace-nowrap flex items-center relative"
                                style={{width: maxNameWidth, paddingLeft: '1rem'}}>
                                {/* Selection indicator triangle - positioned absolutely flush with left border */}
                                {category.isSelected && category.hasTests && (
                                    <div
                                        className="absolute left-0 top-1/2 -translate-y-1/2"
                                        style={{
                                            width: 0,
                                            height: 0,
                                            borderTop: '6px solid transparent',
                                            borderBottom: '6px solid transparent',
                                            borderLeft: '6px solid #70d4e0',
                                        }}
                                    />
                                )}
                                <span
                                    className={`text-sm font-medium ${category.isSelected && category.hasTests ? 'text-mld-lab-trend' : 'text-blue-950'}`}>
                                    {category.name}
                                </span>
                                {/* Info icon */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setTooltipCategory({key: category.key, name: category.name});
                                    }}
                                    className="ml-1.5 p-0.5 text-gray-400 hover:text-mld-lab-trend transition-colors rounded-full hover:bg-gray-100 cursor-pointer"
                                    title={`Learn about ${category.name} score`}>
                                    <Info className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            {/* Right section: Score with bar */}
                            <div className="flex items-center py-3 flex-1 relative pr-4">
                                <div className="flex-1 h-6 relative">
                                    <div
                                        className="h-full rounded-r-md transition-all duration-300 ease-out"
                                        style={{
                                            width: `${category.hasTests ? Math.max(category.value, 5) : 0}%`, // Minimum 5% for visibility
                                            backgroundColor: CATEGORY_COLORS[category.key] || CATEGORY_COLORS.other,
                                        }}
                                    />
                                    <span
                                        className={`text-lg absolute top-1/2 -translate-y-1/2 ${category.isSelected && category.hasTests ? 'text-mld-lab-trend' : 'text-blue-950'}`}
                                        style={{
                                            left: `calc(${category.hasTests ? Math.max(category.value, 5) : 0}% + 8px)`,
                                        }}>
                                        {!category.hasTests || category.value === 0 ? 'N/A' : category.value}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default HorizontalCategoryChart;
